import * as fromProcesses from '../actions/process.actions';
import { GematikProcess } from '../../models/gematik-process.model';

export const ACTIVE_FEATURE_KEY = 'activeProcesses';
export const COMPLETED_FEATURE_KEY = 'completedProcesses';

export interface ProcessState {
  myProcesses: {
    entities: { [id: string]: GematikProcess };
  };
  subscriberProcesses: {
    entities: { [id: string]: GematikProcess };
  };
  loading: boolean;
  loaded: boolean;
  error: boolean;
}

export const initialState: ProcessState = {
  myProcesses: {
    entities: {},
  },
  subscriberProcesses: {
    entities: {},
  },
  loading: false,
  loaded: false,
  error: false,
};

export function processActiveReducer(state = initialState, action: fromProcesses.ProcessActions): ProcessState {
  switch (action.type) {
    case fromProcesses.LOAD_ACTIVE_PROCESSES:
    case fromProcesses.LOAD_ALL_ACTIVE_PROCESSES: {
      return {
        ...state,
        loading: true,
        error: false,
      };
    }
    case fromProcesses.LOAD_ALL_ACTIVE_PROCESSES_SUCCESS: {
      const processes = action.payload;
      const subscriberProcessEntities = processes.subscriberProcesses.reduce(
        (entities: { [id: string]: GematikProcess }, process: GematikProcess) => {
          return {
            ...entities,
            [process.id]: process,
          };
        },
        {
          ...state.subscriberProcesses.entities,
        },
      );

      const myProcessesEntities = processes.myProcesses.reduce(
        (entities: { [id: string]: GematikProcess }, process: GematikProcess) => {
          return {
            ...entities,
            [process.id]: process,
          };
        },
        {
          ...state.myProcesses.entities,
        },
      );

      return {
        ...state,
        loading: false,
        loaded: true,
        subscriberProcesses: {
          entities: subscriberProcessEntities,
        },
        myProcesses: {
          entities: myProcessesEntities,
        },
      };
    }

    case fromProcesses.LOAD_ACTIVE_PROCESSES_SUCCESS: {
      const processes = action.payload;

      // remove
      const myProcesses = Object.entries(state.myProcesses.entities)
        .filter(entity => entity[1].backendUrl !== action.backendUrl)
        .map(entity => entity[1]);

      const subscriberProcesses = Object.entries(state.subscriberProcesses.entities)
        .filter(entity => entity[1].backendUrl !== action.backendUrl)
        .map(entity => entity[1]);

      // add
      myProcesses.push(...action.payload.myProcesses);
      subscriberProcesses.push(...action.payload.subscriberProcesses);

      // reduce
      const myProcessesEntities = myProcesses.reduce(
        (entities: { [id: string]: GematikProcess }, process: GematikProcess) => {
          return {
            ...entities,
            [process.id]: process,
          };
        },
        {},
      );

      const subscriberProcessesEntities = subscriberProcesses.reduce(
        (entities: { [id: string]: GematikProcess }, process: GematikProcess) => {
          return {
            ...entities,
            [process.id]: process,
          };
        },
        {},
      );

      return {
        ...state,
        loading: false,
        loaded: true,
        subscriberProcesses: {
          entities: subscriberProcessesEntities,
        },
        myProcesses: {
          entities: myProcessesEntities,
        },
      };
    }
    case fromProcesses.LOAD_ACTIVE_PROCESSES_FAIL:
    case fromProcesses.LOAD_ALL_ACTIVE_PROCESSES_FAIL: {
      return {
        ...state,
        loading: false,
        loaded: false,
        error: true,
      };
    }

    case fromProcesses.LOAD_ACTIVE_PROCESS: {
      return {
        ...state,
        loading: true,
        error: false,
      };
    }
    case fromProcesses.LOAD_ACTIVE_PROCESS_SUCCESS: {
      const process = action.payload;
      if (!process?.id) {
        return state;
      }
      const myProcessEntities = process.processOwner
        ? { ...state.myProcesses.entities, [process.id]: process as unknown as GematikProcess }
        : state.myProcesses.entities;
      const subscriberProcessEntities = process.processSubscriber
        ? { ...state.subscriberProcesses.entities, [process.id]: process as unknown as GematikProcess }
        : state.subscriberProcesses.entities;
      return {
        ...state,
        loading: false,
        myProcesses: { entities: myProcessEntities },
        subscriberProcesses: { entities: subscriberProcessEntities },
      };
    }
    case fromProcesses.LOAD_ACTIVE_PROCESS_FAIL: {
      return {
        ...state,
        loading: false,
        loaded: false,
      };
    }

    case fromProcesses.PROCESS_ENDED: {
      const { [action.instanceId]: _my, ...remainingMyProcesses } = state.myProcesses.entities;
      const { [action.instanceId]: _sub, ...remainingSubscriberProcesses } = state.subscriberProcesses.entities;
      return {
        ...state,
        myProcesses: { entities: remainingMyProcesses },
        subscriberProcesses: { entities: remainingSubscriberProcesses },
      };
    }
  }
  return state;
}

export const getSubscriberProcessEntities = (state: ProcessState) => state.subscriberProcesses.entities;
export const getMyProcessEntities = (state: ProcessState) => state.myProcesses.entities;

export function processCompletedReducer(state = initialState, action: fromProcesses.ProcessActions): ProcessState {
  switch (action.type) {
    case fromProcesses.LOAD_SUBSCRIBER_COMPLETED_PROCESSES:
    case fromProcesses.LOAD_MY_COMPLETED_PROCESSES: {
      return {
        ...state,
        loading: true,
        error: false,
      };
    }
    case fromProcesses.LOAD_MY_COMPLETED_PROCESSES_SUCCESS: {
      const tasks = action.payload;
      const myProcessesEntities = tasks.myProcesses.reduce(
        (entities: { [id: string]: GematikProcess }, process: GematikProcess) => {
          return {
            ...entities,
            [process.id]: process,
          };
        },
        {
          ...state.myProcesses.entities,
        },
      );
      return {
        ...state,
        loading: false,
        loaded: true,
        myProcesses: {
          entities: myProcessesEntities,
        },
      };
    }
    case fromProcesses.LOAD_SUBSCRIBER_COMPLETED_PROCESSES_SUCCESS: {
      const tasks = action.payload;
      const subscriberProcessesEntities = tasks.subscriberProcesses.reduce(
        (entities: { [id: string]: GematikProcess }, process: GematikProcess) => {
          return {
            ...entities,
            [process.id]: process,
          };
        },
        {
          ...state.subscriberProcesses.entities,
        },
      );
      return {
        ...state,
        loading: false,
        loaded: true,
        subscriberProcesses: {
          entities: subscriberProcessesEntities,
        },
      };
    }
    case fromProcesses.LOAD_SUBSCRIBER_COMPLETED_PROCESSES_FAIL:
    case fromProcesses.LOAD_MY_COMPLETED_PROCESSES_FAIL: {
      return {
        ...state,
        loading: false,
        loaded: false,
        error: true,
      };
    }
  }
  return state;
}
